import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ChartConfiguration } from 'chart.js';
import { CloudData } from 'angular-tag-cloud-module';

import { PrintFilterDataComponent } from '../../shared/components/print-filter-data/print-filter-data.component';
import { EMonths } from '../../utils/enums/months.enum';
import { IEvolutionReport } from '../../utils/interfaces/evolution-report';
import { IEvolutionQuestionReportOLD } from '../../utils/interfaces/evolution-report';
import { IEvolutionReportOLD } from '../../utils/interfaces/evolution-report';
import { IEngagementReportOLD } from '../../utils/interfaces/engagement.report';
import { IIncentiveReport, IIncentiveReportPagination } from '../../utils/interfaces/incentive';
import { IMeter } from '../../utils/interfaces/meter';
import { IPoll } from '../../utils/interfaces/poll';
import { IRankingReport } from '../../utils/interfaces/ranking-report';
import { IReportFilter } from '../../utils/interfaces/report-filter';
import { IShiftComparativeReport } from '../../utils/interfaces/shift-comparative-report';
import { ISummaryReport } from '../../utils/interfaces/summary-report';
import { PrintDocument } from '../print-document';
import { PrintEvolutionComponent } from '../print-evolution/print-evolution.component';
import { PrintIncentiveComponent } from '../print-incentive/print-incentive.component';
import { PrintManagementDiaryComponent } from '../print-management-diary/print-management-diary.component';
import { PrintMeterComponent } from '../print-meter/print-meter.component';
import { EngagementReportBodyComponent } from '../report-bodies/engagement-report-body.component';
import { FeedbackReportBodyComponent } from '../report-bodies/feedback-report-body.component';
import { SummaryReportBodyComponent } from '../report-bodies/summary-report-body.component';
import { PrintShiftComponent } from '../print-shift/print-shift.component';
import { PrintWordCloudComponent } from '../print-word-cloud/print-word-cloud.component';
import { IFeedbackReport } from '../../utils/interfaces/feedback-report';

type ModuleStatus = 'loading' | 'ready' | 'empty' | 'error';

interface ModuleState<T> {
  status: ModuleStatus;
  data: T | null;
  message?: string;
}

interface BranchReportSlices {
  consolidated?: unknown;
  dailyManagerial?: unknown;
  engagement?: unknown;
  feedback?: unknown;
  evolution?: unknown;
  wordCloud?: unknown;
  measurement?: unknown;
  meterEvolution?: unknown;
  managementDiaryEvolution?: unknown;
  shiftComparative?: unknown;
  incentive?: unknown;
  searchOnline?: unknown;
}

interface AggregatedPayloadEntry {
  id: string;
  label: string;
  branchReports: BranchReportSlices;
}

interface AggregatedDeviceShiftView {
  id: string;
  label: string;
  shifts: AggregatedEntryView[];
}

interface AggregatedEntryView {
  id: string;
  label: string;
  summaryState: ModuleState<ISummaryReport>;
  managementDiaryState: ModuleState<ManagementDiarySection>;
  engagementState: ModuleState<ChartConfiguration<'bar'>['data']>;
  feedbackState: ModuleState<IFeedbackReport[]>;
  evolutionState: ModuleState<IEvolutionReport[]>;
  wordCloudState: ModuleState<WordCloudSection>;
  meterState: ModuleState<MeterSection>;
  shiftState: ModuleState<ShiftSection>;
  incentiveState: ModuleState<IIncentiveReportPagination[]>;
  searchOnlineState: ModuleState<IFeedbackReport[]>;
}

type EntryModuleKey =
  | 'summaryState'
  | 'managementDiaryState'
  | 'engagementState'
  | 'feedbackState'
  | 'evolutionState'
  | 'wordCloudState'
  | 'meterState'
  | 'shiftState'
  | 'incentiveState'
  | 'searchOnlineState';

interface ManagementDiarySection {
  dataSummary: ISummaryReport | null;
  dataEvolution: IEvolutionReport | null;
  dataRanking: IRankingReport[];
  wordcloud: CloudData[];
}

interface ManagementDiaryFallbackPayload {
  dataEvolution: IEvolutionReport | null;
  dataSummary: ISummaryReport | null;
  dataRanking: unknown;
  wordcloud: CloudData[];
}

interface WordCloudSection {
  data: CloudData[];
  title: string;
}

interface MeterSection {
  dataEvolution: IEvolutionReport | null;
  dataMeter: IMeter | null;
}

interface ShiftSection {
  data: ChartConfiguration<'bar'>['data'];
  totalResponses: number;
  overallSatisfaction: number;
}

@Component({
  selector: 'app-print-aggregated',
  standalone: true,
  imports: [
    CommonModule,
    PrintFilterDataComponent,
    SummaryReportBodyComponent,
    EngagementReportBodyComponent,
    FeedbackReportBodyComponent,
    PrintManagementDiaryComponent,
    PrintEvolutionComponent,
    PrintWordCloudComponent,
    PrintMeterComponent,
    PrintShiftComponent,
    PrintIncentiveComponent,
  ],
  templateUrl: 'print-aggregated.component.html',
  styleUrl: 'print-aggregated.component.scss',
})
export class PrintAggregatedComponent
  extends PrintDocument
  implements OnInit, AfterViewInit {
  orientation = 'portrait';
  filter!: IReportFilter;
  poll: IPoll | null = null;
  branchEntries: AggregatedEntryView[] = [];
  deviceEntries: AggregatedEntryView[] = [];
  deviceShiftEntries: AggregatedDeviceShiftView[] = [];
  rootMessage = 'Não há dados do consolidado para exibir.';

  private readonly titleService = inject(Title);

  private aggregatedData: unknown = null;
  private aggregatedByDevicesData: unknown = null;
  private aggregatedByDevicesAndShiftData: unknown = null;
  private initialManagementDiaryData: ManagementDiaryFallbackPayload | null = null;
  private viewInitialized = false;
  modulesResolved = false;
  private readySignaled = false;

  ngOnInit(): void {
    this.handleData();
    this.loadModules();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.reportReadyData();
  }

  protected override handleData(): void {
    const {
      aggregatedData,
      aggregatedByDevicesData,
      aggregatedByDevicesAndShiftData,
      initialManagementDiaryData,
      filter,
      poll,
    } =
      this.activatedRoute.snapshot.data['data'];

    this.aggregatedData = aggregatedData;
    this.aggregatedByDevicesData = aggregatedByDevicesData ?? null;
    this.aggregatedByDevicesAndShiftData = aggregatedByDevicesAndShiftData ?? null;
    this.initialManagementDiaryData = initialManagementDiaryData ?? null;
    this.filter = filter;
    this.poll = poll;

    this.titleService.setTitle(
      this.changeTitlePage(
        'Relatório consolidado',
        this.filter.startDate,
        this.filter.finishDate,
      ),
    );
  }

  protected override reportReadyData(): void {
    if (!this.viewInitialized || !this.modulesResolved || this.readySignaled) {
      return;
    }

    this.readySignaled = true;
    setTimeout(() => {
      this.printDocumentService.onDataReady();
    }, 3000);
  }

  private loadModules(): void {
    const parsedPayload = normalizeAggregatedPayload(this.aggregatedData);

    this.rootMessage = parsedPayload.message || this.rootMessage;
    this.branchEntries = parsedPayload.entries.map((entry) =>
      buildEntryView(
        entry,
        this.filter,
        this.poll,
        entry.id === 'aggregated-report'
          ? normalizeManagementDiaryFallback(this.initialManagementDiaryData)
          : null,
      ),
    );

    const rawDeviceData = Array.isArray(this.aggregatedByDevicesData)
      ? (this.aggregatedByDevicesData as unknown[])
      : [];

    this.deviceEntries = rawDeviceData.map((item, index) => {
      const record = isRecord(item) ? item : {};
      const branchName = typeof record['branchName'] === 'string' ? record['branchName'] : '';
      const branchDevice = typeof record['branchDevice'] === 'string' ? record['branchDevice'] : '';
      const label =
        [branchName, branchDevice].filter(Boolean).join(' • ') ||
        `Filial/dispositivo ${index + 1}`;
      const branchReports = isRecord(record['branchReports'])
        ? (record['branchReports'] as BranchReportSlices)
        : {};
      const entry: AggregatedPayloadEntry = {
        id:
          (typeof record['branchCode'] === 'string' ? record['branchCode'] : '') ||
          `device-entry-${index + 1}`,
        label,
        branchReports,
      };
      return buildEntryView(entry, this.filter, this.poll);
    });

    this.deviceShiftEntries = normalizeAggregatedByDevicesAndShiftsPayload(
      this.aggregatedByDevicesAndShiftData,
      this.filter,
      this.poll,
    );

    this.modulesResolved = true;
    this.reportReadyData();
  }

  hasVisibleModule(moduleKey: EntryModuleKey): boolean {
    return this.branchEntries.some(
      (entry) => entry[moduleKey].status !== 'empty',
    );
  }

  hasVisibleDeviceModule(moduleKey: EntryModuleKey): boolean {
    return this.deviceEntries.some(
      (entry) => entry[moduleKey].status !== 'empty',
    );
  }
}

function normalizeAggregatedByDevicesAndShiftsPayload(
  source: unknown,
  filter: IReportFilter,
  poll: IPoll | null,
): AggregatedDeviceShiftView[] {
  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .flatMap((item, sourceIndex) => normalizeDeviceShiftGroups(item, sourceIndex))
    .map((group) => ({
      id: group.id,
      label: group.label,
      shifts: group.shifts.map((entry) => buildEntryView(entry, filter, poll)),
    }))
    .filter((group) => group.shifts.length > 0);
}

function normalizeDeviceShiftGroups(
  source: unknown,
  sourceIndex: number,
): Array<{
  id: string;
  label: string;
  shifts: AggregatedPayloadEntry[];
}> {
  const record = isRecord(source) ? source : {};
  const devices = Array.isArray(record['devices']) ? record['devices'] : [];

  return devices
    .map((device, deviceIndex) => {
      const deviceRecord = isRecord(device) ? device : {};
      const shifts = Array.isArray(deviceRecord['shifts']) ? deviceRecord['shifts'] : [];

      return {
        id: buildDeviceShiftGroupId(record, deviceRecord, sourceIndex, deviceIndex),
        label: buildDeviceShiftGroupLabel(record, deviceRecord, sourceIndex, deviceIndex),
        shifts: shifts
          .map((shift, shiftIndex) =>
            normalizeShiftEntry(
              shift,
              deviceRecord,
              record,
              sourceIndex,
              deviceIndex,
              shiftIndex,
            ),
          )
          .filter((entry): entry is AggregatedPayloadEntry => entry !== null),
      };
    })
    .filter((group) => group.shifts.length > 0);
}

function normalizeShiftEntry(
  source: unknown,
  deviceRecord: Record<string, unknown>,
  parentRecord: Record<string, unknown>,
  sourceIndex: number,
  deviceIndex: number,
  shiftIndex: number,
): AggregatedPayloadEntry | null {
  const record = isRecord(source) ? source : {};
  const branchReports = isRecord(record['branchReports'])
    ? (record['branchReports'] as BranchReportSlices)
    : null;

  if (!branchReports) {
    return null;
  }

  return {
    id: buildShiftEntryId(
      record,
      deviceRecord,
      parentRecord,
      sourceIndex,
      deviceIndex,
      shiftIndex,
    ),
    label: buildShiftEntryLabel(record, shiftIndex),
    branchReports,
  };
}

function buildDeviceShiftGroupId(
  parentRecord: Record<string, unknown>,
  deviceRecord: Record<string, unknown>,
  sourceIndex: number,
  deviceIndex: number,
): string {
  const groupId = [
    readString(parentRecord, 'id') || readString(parentRecord, 'branchId'),
    readString(deviceRecord, 'id') || readString(deviceRecord, 'deviceId'),
  ]
    .filter(Boolean)
    .join('-');

  return groupId || `device-shift-group-${sourceIndex + 1}-${deviceIndex + 1}`;
}

function buildDeviceShiftGroupLabel(
  parentRecord: Record<string, unknown>,
  deviceRecord: Record<string, unknown>,
  sourceIndex: number,
  deviceIndex: number,
): string {
  const branchLabel =
    describeScope(parentRecord['branch']) ||
    describeScope(parentRecord['branches']) ||
    readString(parentRecord, 'branchName');
  const deviceLabel =
    describeScope(deviceRecord['device']) ||
    describeScope(deviceRecord) ||
    readString(deviceRecord, 'deviceName');
  const labels = [branchLabel, deviceLabel].filter(Boolean);

  return labels.length
    ? labels.join(' • ')
    : `Filial/dispositivo ${sourceIndex + 1}.${deviceIndex + 1}`;
}

function buildShiftEntryId(
  shiftRecord: Record<string, unknown>,
  deviceRecord: Record<string, unknown>,
  parentRecord: Record<string, unknown>,
  sourceIndex: number,
  deviceIndex: number,
  shiftIndex: number,
): string {
  const entryId = [
    readString(parentRecord, 'id') || readString(parentRecord, 'branchId'),
    readString(deviceRecord, 'id') || readString(deviceRecord, 'deviceId'),
    readString(shiftRecord, 'id') || readString(shiftRecord, 'shiftId'),
  ]
    .filter(Boolean)
    .join('-');

  return entryId || `device-shift-entry-${sourceIndex + 1}-${deviceIndex + 1}-${shiftIndex + 1}`;
}

function buildShiftEntryLabel(
  shiftRecord: Record<string, unknown>,
  shiftIndex: number,
): string {
  return (
    readString(shiftRecord, 'shiftDescription') ||
    readString(shiftRecord, 'description') ||
    readString(shiftRecord, 'shiftName') ||
    readString(shiftRecord, 'name') ||
    `Turno ${shiftIndex + 1}`
  );
}

function loadingState<T>(): ModuleState<T> {
  return { status: 'loading', data: null };
}

function readyState<T>(data: T): ModuleState<T> {
  return { status: 'ready', data };
}

function emptyState<T>(message: string): ModuleState<T> {
  return { status: 'empty', data: null, message };
}

function errorState<T>(message: string): ModuleState<T> {
  return { status: 'error', data: null, message };
}

function classifyState<T>(
  data: T,
  hasData: (value: T) => boolean,
  emptyMessage: string,
): ModuleState<T> {
  return hasData(data) ? readyState(data) : emptyState(emptyMessage);
}

function normalizeAggregatedPayload(source: unknown): {
  entries: AggregatedPayloadEntry[];
  message: string;
} {
  if (source === null || source === undefined) {
    return {
      entries: [],
      message: 'Não foi possível carregar o relatório.',
    };
  }

  if (Array.isArray(source)) {
    return {
      entries: source.map((item, index) => normalizeAggregatedEntry(item, index)),
      message: '',
    };
  }

  if (isRecord(source)) {
    const consolidated = isRecord(source['consolidated']) ? source['consolidated'] : null;
    const label =
      consolidated && typeof consolidated['title'] === 'string'
        ? consolidated['title']
        : 'Relatório Consolidado';

    return {
      entries: [{ id: 'aggregated-report', label, branchReports: source as BranchReportSlices }],
      message: '',
    };
  }

  return {
    entries: [],
    message: 'Não foi possível carregar o relatório.',
  };
}

function normalizeAggregatedEntry(
  source: unknown,
  index: number,
): AggregatedPayloadEntry {
  const record = isRecord(source) ? source : {};
  const branchReports = isRecord(record['branchReports'])
    ? (record['branchReports'] as BranchReportSlices)
    : {};

  return {
    id: buildEntryId(record, index),
    label: buildEntryLabel(record, index),
    branchReports,
  };
}

function buildEntryView(
  entry: AggregatedPayloadEntry,
  filter: IReportFilter,
  poll: IPoll | null,
  managementDiaryFallback: ManagementDiarySection | null = null,
): AggregatedEntryView {
  const summary = normalizeSummaryReport(entry.branchReports.consolidated);
  const engagement = normalizeEngagementData(entry.branchReports.engagement);
  const feedback = normalizeFeedbackReports(entry.branchReports.feedback);
  const evolution = normalizeEvolutionReports(entry.branchReports.evolution, filter);
  const wordCloud = normalizeWordCloudSection(
    entry.branchReports.wordCloud,
    poll,
    entry.branchReports.dailyManagerial,
  );
  // Quando meterEvolution está presente (janela de 335 dias vinda do backend),
  // construímos um filtro com as mesmas datas que o standalone usa — espelhando a lógica:
  //   initialDate = new Date(new Date().setDate(new Date().getDate() - 335))
  // Isso garante que getMonthsInRange gere TODOS os meses do intervalo (inclusive os
  // com zero respostas), mantendo a linha de tendência contínua no gráfico.
  const meterEvolutionSource = entry.branchReports.meterEvolution ?? entry.branchReports.evolution;
  let meterFilter: IReportFilter | null;
  if (entry.branchReports.meterEvolution != null) {
    const today = new Date();
    const meterStart = new Date(today);
    meterStart.setDate(today.getDate() - 335);
    meterFilter = {
      ...filter,
      startDate: meterStart.toISOString().split('T')[0] + 'T00:00:00.000Z',
      finishDate: today.toISOString().split('T')[0] + 'T00:00:00.000Z',
    };
  } else {
    meterFilter = filter;
  }
  const meter = normalizeMeterSection(
    entry.branchReports.measurement,
    meterEvolutionSource,
    poll,
    meterFilter,
  );
  const shiftData = normalizeShiftReports(entry.branchReports.shiftComparative);
  const incentive = normalizeIncentiveSlice(entry.branchReports.incentive);
  const searchOnline = normalizeSearchOnlineReports(entry.branchReports.searchOnline);
  const managementDiary = buildManagementDiarySection(
    entry.branchReports,
    poll,
    filter,
    managementDiaryFallback,
  );

  return {
    id: entry.id,
    label: entry.label,
    summaryState: classifyState(
      summary,
      hasSummaryData,
      'Não há dados para exibir',
    ),
    managementDiaryState: classifyState(
      managementDiary,
      hasManagementDiaryData,
      'Não há dados do diário gerencial para exibir',
    ),
    engagementState: classifyState(
      engagement,
      hasEngagementData,
      'Não há dados para exibir',
    ),
    feedbackState: classifyState(
      feedback,
      hasFeedbackData,
      'Não há dados de feedback para exibir',
    ),
    evolutionState: classifyState(
      evolution,
      hasEvolutionData,
      'Não há dados de evolução para exibir',
    ),
    wordCloudState: classifyState(
      wordCloud,
      hasWordCloudData,
      'Não há dados de wordcloud para exibir',
    ),
    meterState: classifyState(
      meter,
      hasMeterData,
      'Não há dados de medidor para exibir',
    ),
    shiftState: buildShiftState(
      shiftData,
      filter,
      'Não há dados de turno para exibir',
    ),
    incentiveState: classifyState(
      incentive,
      hasIncentiveData,
      'Não há incentivo configurado no período.',
    ),
    searchOnlineState: classifyState(
      searchOnline,
      hasFeedbackData,
      'Não há dados da Pesquisa Online para exibir',
    ),
  };
}

function normalizeSummaryReport(source: unknown): ISummaryReport {
  if (isSummaryReport(source)) {
    return {
      ...source,
      pages: [...source.pages].sort((a, b) => a.pageNumber - b.pageNumber),
    };
  }

  const nested = getNestedRecord(source, 'data', 'report', 'consolidated');

  if (isSummaryReport(nested)) {
    return {
      ...nested,
      pages: [...nested.pages].sort((a, b) => a.pageNumber - b.pageNumber),
    };
  }

  return {
    title: '',
    pages: [],
  };
}

function normalizeEngagementData(
  source: unknown,
): ChartConfiguration<'bar'>['data'] {
  if (isEngagementChartData(source)) {
    return source;
  }

  if (isEngagementReportOldList(source)) {
    return transformEngagementReportData(source);
  }

  const nested = getNestedRecord(source, 'data', 'report', 'engagement');

  if (isEngagementChartData(nested)) {
    return nested;
  }

  if (isEngagementReportOldList(nested)) {
    return transformEngagementReportData(nested);
  }

  return { labels: [], datasets: [] };
}

function normalizeFeedbackReports(source: unknown): IFeedbackReport[] {
  if (isFeedbackReportList(source)) {
    return source.map((item) => sanitizeFeedbackReport(item));
  }

  if (!isRecord(source)) {
    return [];
  }

  return Object.entries(source).reduce<IFeedbackReport[]>((acc, [key, value]) => {
    const normalized = normalizeFeedbackEntry(value, key);

    if (normalized) {
      acc.push(normalized);
    }

    return acc;
  }, []);
}

function normalizeSearchOnlineReports(source: unknown): IFeedbackReport[] {
  return normalizeFeedbackReports(source).filter(
    (item) => item.responsesOpens?.length > 0,
  );
}

function normalizeFeedbackEntry(
  source: unknown,
  key: string,
): IFeedbackReport | null {
  if (!isRecord(source)) {
    return null;
  }

  const responsesSource = Array.isArray(source['responsesOpens'])
    ? source['responsesOpens']
    : [];

  return {
    title: readString(source, 'title') || readString(source, 'question') || key,
    page: readNumber(source, 'page', 1),
    take: readNumber(source, 'take', responsesSource.length || 50),
    questionId: readString(source, 'questionId') || key,
    count: readNumber(source, 'count', responsesSource.length),
    responsesOpens: responsesSource.map((item) => normalizeFeedbackResponse(item, source)),
  };
}

function sanitizeFeedbackReport(report: IFeedbackReport): IFeedbackReport {
  return {
    ...report,
    responsesOpens: (report.responsesOpens ?? []).map((item) =>
      normalizeFeedbackResponse(item),
    ),
  };
}

function normalizeFeedbackResponse(
  source: unknown,
  parent?: Record<string, unknown>,
): IFeedbackReport['responsesOpens'][number] {
  const record = isRecord(source) ? source : {};
  const hasShiftData = parent?.['hasShiftData'] === true;
  const shiftLabel = readString(record, 'shift');
  const deviceLabel = readString(record, 'device');

  return {
    answerMainQuestion:
      readString(record, 'answerMainQuestion') || readString(record, 'question') || '--',
    answerOpen: readString(record, 'answerOpen'),
    branchCode: readString(record, 'branchCode'),
    branchId: readString(record, 'branchId'),
    branchName: readString(record, 'branchName'),
    createdAt: readString(record, 'createdAt'),
    device: deviceLabel || (hasShiftData ? shiftLabel : shiftLabel || '--'),
  };
}

function normalizeEvolutionReports(
  source: unknown,
  filter?: IReportFilter | null,
): IEvolutionReport[] {
  if (Array.isArray(source)) {
    return source.filter(isEvolutionReport);
  }

  if (isEvolutionReport(source)) {
    return [source];
  }

  if (isEvolutionReportOld(source)) {
    return transformLegacyEvolutionReports(source, filter);
  }

  const nested = getNestedRecord(source, 'data', 'report', 'evolution');

  if (Array.isArray(nested)) {
    return nested.filter(isEvolutionReport);
  }

  if (isEvolutionReportOld(nested)) {
    return transformLegacyEvolutionReports(nested, filter);
  }

  return isEvolutionReport(nested) ? [nested] : [];
}

function normalizeWordCloudSection(
  source: unknown,
  poll: IPoll | null,
  dailyManagerialSource?: unknown,
): WordCloudSection {
  const dailyManagerialWordCloud = getNestedRecord(
    dailyManagerialSource,
    'wordcloud',
    'wordCloud',
  );
  const wordCloudData =
    normalizeWordCloudData(source) || normalizeWordCloudData(dailyManagerialWordCloud);

  return {
    data: wordCloudData ?? [],
    title: readWordCloudTitle(source) || extractPrimaryQuestionTitle(poll),
  };
}

function normalizeWordCloudData(source: unknown): CloudData[] | null {
  if (isCloudDataList(source)) {
    return source;
  }

  if (!isRecord(source)) {
    return null;
  }

  const wordCloud = source['wordCloud'];
  return isCloudDataList(wordCloud) ? wordCloud : null;
}

function readWordCloudTitle(source: unknown): string {
  if (!isRecord(source)) {
    return '';
  }

  return readString(source, 'title') || readString(source, 'questionTitle');
}

function normalizeMeterSection(
  measurementSource: unknown,
  evolutionSource: unknown,
  poll: IPoll | null,
  filter?: IReportFilter | null,
): MeterSection {
  const evolution = normalizeEvolutionReports(evolutionSource, filter)[0] ?? null;
  const titledEvolution = evolution
    ? {
      ...evolution,
      title: extractPrimaryQuestionTitle(poll) || evolution.title,
    }
    : null;

  return {
    dataEvolution: titledEvolution,
    dataMeter: normalizeMeterData(extractMeterPayload(measurementSource)),
  };
}

function extractMeterPayload(source: unknown): IMeter | null {
  if (isMeter(source)) {
    return source;
  }

  const nested = getNestedRecord(source, 'data', 'measurement', 'report');
  return isMeter(nested) ? nested : null;
}

function normalizeShiftReports(source: unknown): IShiftComparativeReport[] {
  if (Array.isArray(source)) {
    return source.filter(isShiftComparativeReport);
  }

  const nested = getNestedRecord(source, 'data', 'shiftComparative', 'report');
  return Array.isArray(nested)
    ? nested.filter(isShiftComparativeReport)
    : [];
}

function normalizeIncentiveSlice(source: unknown): IIncentiveReportPagination[] {
  if (Array.isArray(source)) {
    return normalizeIncentiveData(source.filter(isIncentiveReport));
  }

  const nested = getNestedRecord(source, 'data', 'report', 'incentive');
  return Array.isArray(nested)
    ? normalizeIncentiveData(nested.filter(isIncentiveReport))
    : [];
}

function buildManagementDiarySection(
  branchReports: BranchReportSlices,
  poll: IPoll | null,
  filter: IReportFilter,
  fallback: ManagementDiarySection | null = null,
): ManagementDiarySection {
  const fallbackSection = fallback ?? emptyManagementDiarySection();

  // managementDiaryEvolution contém dados diários (dia/mês) vindos do EvolutionDaysService.
  // hasDailyEvolutionDetails detecta o campo "day" e usa transformLegacyEvolutionDays,
  // que constrói os labels DD/Mon diretamente dos dados — sem precisar do filter de datas.
  const managementDiaryEvolutionSource =
    branchReports.managementDiaryEvolution ??
    getNestedRecord(branchReports.dailyManagerial, 'dataEvolution', 'evolution') ??
    branchReports.dailyManagerial;

  const dailyManagerialEvolution =
    normalizeEvolutionReports(
      managementDiaryEvolutionSource,
      branchReports.managementDiaryEvolution != null ? null : filter,
    )[0] ?? null;

  const summary = normalizeSummaryReport(branchReports.consolidated);
  const ranking = normalizeRankingReports(branchReports.dailyManagerial);
  const wordcloud =
    normalizeWordCloudData(
      getNestedRecord(branchReports.dailyManagerial, 'wordcloud', 'wordCloud') ??
      branchReports.wordCloud,
    ) ?? [];
  const evolution =
    dailyManagerialEvolution ?? normalizeEvolutionReports(branchReports.evolution, filter)[0] ?? null;

  return {
    dataSummary: hasSummaryData(summary) ? summary : fallbackSection.dataSummary,
    dataEvolution: hasEvolutionSeries(evolution) ? evolution : fallbackSection.dataEvolution,
    dataRanking: ranking.length ? ranking : fallbackSection.dataRanking,
    wordcloud: wordcloud.length ? wordcloud : fallbackSection.wordcloud,
  };
}

function normalizeManagementDiaryFallback(
  source: ManagementDiaryFallbackPayload | null,
): ManagementDiarySection | null {
  if (!source) {
    return null;
  }

  return {
    dataSummary: isSummaryReport(source.dataSummary) ? source.dataSummary : null,
    dataEvolution: isEvolutionReport(source.dataEvolution) ? source.dataEvolution : null,
    dataRanking: normalizeRankingReports(source.dataRanking),
    wordcloud: isCloudDataList(source.wordcloud) ? source.wordcloud : [],
  };
}

function emptyManagementDiarySection(): ManagementDiarySection {
  return {
    dataSummary: null,
    dataEvolution: null,
    dataRanking: [],
    wordcloud: [],
  };
}

function transformEngagementReportData(
  source: IEngagementReportOLD[],
): ChartConfiguration<'bar'>['data'] {
  return {
    labels: source.some((item) => item.year)
      ? source.map((item) => `${item.month}/${item.year}`)
      : [],
    datasets: [
      {
        label: 'Avaliações completas',
        data: source.map((item) => item.percentCompleted),
        backgroundColor: '#BE0013',
        borderColor: '#BE0013',
        borderWidth: 1,
        custom: source.map((item) => item.totalCompleted).toString(),
      },
      {
        label: 'Avaliações incompletas',
        data: source.map((item) => item.percentIncomplete),
        backgroundColor: '#8E9199',
        borderColor: '#8E9199',
        borderWidth: 1,
        custom: source.map((item) => item.totalIncomplete).toString(),
      },
    ] as any,
  };
}

function transformLegacyEvolutionReports(
  source: IEvolutionReportOLD,
  filter?: IReportFilter | null,
): IEvolutionReport[] {
  if (hasDailyEvolutionDetails(source)) {
    return [transformLegacyEvolutionDays(source)];
  }

  const labels = getLegacyEvolutionLabels(source, filter);
  const reports: IEvolutionReport[] = [];
  const firstQuestion = source.firstQuestion[0];

  if (firstQuestion) {
    reports.push(transformLegacyEvolutionFirstQuestion(firstQuestion, labels));
  }

  source.othersQuestions.forEach((question) => {
    reports.push(transformLegacyEvolutionOtherQuestion(question, labels));
  });

  return reports;
}

function hasDailyEvolutionDetails(source: IEvolutionReportOLD): boolean {
  const details = source.firstQuestion[0]?.detailsOthersQuestion ?? [];
  return details.some((item) => typeof item.day === 'number');
}

function transformLegacyEvolutionDays(source: IEvolutionReportOLD): IEvolutionReport {
  const firstQuestion = source.firstQuestion[0];
  // A chave da data é ano + mês + dia: sem o ano, dias iguais de anos
  // diferentes colidiam no mesmo ponto do gráfico.
  const details = [...(firstQuestion?.detailsOthersQuestion ?? [])].sort(
    (left, right) =>
      (left.year ?? 0) - (right.year ?? 0) ||
      left.month - right.month ||
      (left.day ?? 0) - (right.day ?? 0),
  );

  const spansMultipleYears = new Set(details.map((item) => item.year)).size > 1;

  return {
    title: firstQuestion?.title ?? '',
    responses: firstQuestion?.countResponses ?? 0,
    labels: details.map((item) => buildDayLabel(item, spansMultipleYears)),
    data: [
      {
        values: details.map((item) => item.percentage),
        label: 'Satisfação',
      },
    ],
  };
}

function padDay(day: number | undefined): string {
  const value = day ?? 0;
  return value < 10 ? `0${value}` : `${value}`;
}

/**
 * Label da data no formato 'DD/Mês', com o ano anexado quando o período
 * cruza mais de um ano e os labels colidiriam.
 */
function buildDayLabel(
  detail: { day?: number; month: number; year?: number },
  includeYear: boolean,
): string {
  const label = `${padDay(detail.day)}/${EMonths[detail.month]}`;

  return includeYear && detail.year ? `${label}/${detail.year}` : label;
}

function getLegacyEvolutionLabels(
  source: IEvolutionReportOLD,
  filter?: IReportFilter | null,
): string[] {
  if (filter) {
    return getMonthsInRange(new Date(filter.startDate), new Date(filter.finishDate));
  }

  const labels = new Set<string>();

  source.firstQuestion.forEach((question) => {
    (question.detailsOthersQuestion ?? []).forEach((detail) => {
      labels.add(`${EMonths[detail.month]}/${detail.year}`);
    });
  });

  source.othersQuestions.forEach((question) => {
    (question.detailsOthersQuestion ?? []).forEach((detail) => {
      labels.add(`${EMonths[detail.month]}/${detail.year}`);
    });
  });

  return [...labels];
}

function transformLegacyEvolutionFirstQuestion(
  firstQuestion: IEvolutionQuestionReportOLD,
  labels: string[],
): IEvolutionReport {
  const values = labels.map((label) => {
    const detail = (firstQuestion.detailsOthersQuestion ?? []).find(
      (item) => `${EMonths[item.month]}/${item.year}` === label,
    );

    return detail?.percentage ?? 0;
  });

  return {
    title: firstQuestion.title ?? '',
    responses: firstQuestion.countResponses ?? 0,
    labels: [...labels],
    data: [
      {
        values,
        label: 'Satisfação',
      },
    ],
  };
}

function transformLegacyEvolutionOtherQuestion(
  question: IEvolutionQuestionReportOLD,
  labels: string[],
): IEvolutionReport {
  const answerLabels = [
    ...new Set(
      (question.detailsOthersQuestion ?? []).map((detail) => detail.answer ?? 'Sem resposta'),
    ),
  ];

  return {
    title: question.title,
    responses: question.countResponses,
    labels: [...labels],
    data: answerLabels.map((answerLabel) => ({
      label: answerLabel,
      values: labels.map((label) => {
        const detail = (question.detailsOthersQuestion ?? []).find(
          (item) =>
            (item.answer ?? 'Sem resposta') === answerLabel &&
            `${EMonths[item.month]}/${item.year}` === label,
        );

        return detail?.percentage ?? 0;
      }),
    })),
  };
}

function getMonthsInRange(start: Date, finish: Date): string[] {
  const months: string[] = [];
  const current = new Date(start);

  while (current <= finish) {
    months.push(`${EMonths[current.getMonth() + 1]}/${current.getFullYear()}`);
    current.setMonth(current.getMonth() + 1);
  }

  const finishLabel = `${EMonths[finish.getMonth() + 1]}/${finish.getFullYear()}`;

  if (!months.includes(finishLabel)) {
    months.push(finishLabel);
  }

  return months;
}

function normalizeRankingReports(source: unknown): IRankingReport[] {
  if (Array.isArray(source)) {
    return source.filter(isRankingReport);
  }

  const nested = getNestedRecord(
    source,
    'dataRanking',
    'ranking',
    'dailyManagerial',
  );

  return Array.isArray(nested) ? nested.filter(isRankingReport) : [];
}

function hasSummaryData(data: ISummaryReport): boolean {
  return data.pages.some((page) => page.questions?.length);
}

function hasManagementDiaryData(data: ManagementDiarySection): boolean {
  return (
    hasSummaryDataOrNull(data.dataSummary) ||
    hasEvolutionSeries(data.dataEvolution) ||
    hasRankingData(data.dataRanking) ||
    hasCloudData(data.wordcloud)
  );
}

function hasEngagementData(data: ChartConfiguration<'bar'>['data']): boolean {
  return !!data.labels?.length && !!data.datasets?.length;
}

function hasFeedbackData(data: IFeedbackReport[]): boolean {
  return data.some((item) => item.responsesOpens?.length);
}

function hasEvolutionData(data: IEvolutionReport[]): boolean {
  return data.some((item) => hasEvolutionSeries(item));
}

function hasWordCloudData(data: WordCloudSection): boolean {
  return hasCloudData(data.data);
}

function hasMeterData(data: MeterSection): boolean {
  return !!data.dataMeter || hasEvolutionSeries(data.dataEvolution);
}

function hasIncentiveData(data: IIncentiveReportPagination[]): boolean {
  return data.length > 0;
}

function hasSummaryDataOrNull(data: ISummaryReport | null): boolean {
  return !!data && hasSummaryData(data);
}

function hasEvolutionSeries(data: IEvolutionReport | null): boolean {
  return !!data && !!data.labels?.length && !!data.data?.length;
}

function hasRankingData(data: IRankingReport[]): boolean {
  return data.some((item) =>
    item.dailyManagerialList.some((dailyData) => dailyData.dailyManagerialData.length),
  );
}

function hasCloudData(data: CloudData[]): boolean {
  return data.length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function getNestedRecord(source: unknown, ...keys: string[]): unknown {
  for (const key of keys) {
    if (isRecord(source) && key in source) {
      return source[key];
    }
  }

  return null;
}

function readString(
  source: Record<string, unknown>,
  key: string,
  fallback = '',
): string {
  const value = source[key];
  return typeof value === 'string' ? value : fallback;
}

function readNumber(
  source: Record<string, unknown>,
  key: string,
  fallback: number,
): number {
  const value = source[key];
  return typeof value === 'number' ? value : fallback;
}

function buildEntryId(source: Record<string, unknown>, index: number): string {
  return (
    readString(source, 'id') ||
    readString(source, 'branchId') ||
    readString(source, 'deviceId') ||
    `branch-report-${index + 1}`
  );
}

function buildEntryLabel(source: Record<string, unknown>, index: number): string {
  const branchLabel = describeScope(source['branches']) || describeScope(source['branch']);
  const deviceLabel = describeScope(source['devices']) || describeScope(source['device']);
  const labels = [branchLabel, deviceLabel].filter(Boolean);

  return labels.length ? labels.join(' • ') : `Filial/dispositivo ${index + 1}`;
}

function describeScope(source: unknown): string {
  if (typeof source === 'string') {
    return source;
  }

  if (!isRecord(source)) {
    return '';
  }

  const code =
    readString(source, 'branchCode') ||
    readString(source, 'deviceCode') ||
    readString(source, 'code');
  const name =
    readString(source, 'branchName') ||
    readString(source, 'deviceName') ||
    readString(source, 'name') ||
    readString(source, 'description');

  return [code, name].filter(Boolean).join(' - ');
}

function isSummaryReport(value: unknown): value is ISummaryReport {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as ISummaryReport;
  return (
    typeof candidate.title === 'string' &&
    Array.isArray(candidate.pages) &&
    candidate.pages.every(
      (page) =>
        typeof page.pageNumber === 'number' &&
        Array.isArray(page.questions),
    )
  );
}

function isEngagementChartData(
  value: unknown,
): value is ChartConfiguration<'bar'>['data'] {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as ChartConfiguration<'bar'>['data'];
  return Array.isArray(candidate.labels) && Array.isArray(candidate.datasets);
}

function isEngagementReportOldList(value: unknown): value is IEngagementReportOLD[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as IEngagementReportOLD).month === 'string' &&
        typeof (item as IEngagementReportOLD).year === 'string' &&
        typeof (item as IEngagementReportOLD).percentCompleted === 'number' &&
        typeof (item as IEngagementReportOLD).percentIncomplete === 'number' &&
        typeof (item as IEngagementReportOLD).totalCompleted === 'number' &&
        typeof (item as IEngagementReportOLD).totalIncomplete === 'number',
    )
  );
}

function isFeedbackReportList(value: unknown): value is IFeedbackReport[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item &&
        typeof item.title === 'string' &&
        Array.isArray(item.responsesOpens),
    )
  );
}

function isEvolutionReport(value: unknown): value is IEvolutionReport {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as IEvolutionReport;
  return (
    typeof candidate.title === 'string' &&
    Array.isArray(candidate.labels) &&
    Array.isArray(candidate.data)
  );
}

function isEvolutionReportOld(value: unknown): value is IEvolutionReportOLD {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as IEvolutionReportOLD;
  return Array.isArray(candidate.firstQuestion) && Array.isArray(candidate.othersQuestions);
}

function isCloudDataList(value: unknown): value is CloudData[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as CloudData).text === 'string' &&
        typeof (item as CloudData).weight === 'number',
    )
  );
}

function isMeter(value: unknown): value is IMeter {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as IMeter;
  return (
    typeof candidate.measurmentPercentage === 'number' &&
    typeof candidate.responseCount === 'number' &&
    Array.isArray(candidate.measurmentDatas)
  );
}

function isShiftComparativeReport(
  value: unknown,
): value is IShiftComparativeReport {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as IShiftComparativeReport;
  return (
    typeof candidate.branchName === 'string' &&
    typeof candidate.totalResponses === 'number' &&
    Array.isArray(candidate.shiftData)
  );
}

function isIncentiveReport(value: unknown): value is IIncentiveReport {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as IIncentiveReport;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    Array.isArray(candidate.draws)
  );
}

function isRankingReport(value: unknown): value is IRankingReport {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as IRankingReport;
  return Array.isArray(candidate.dailyManagerialList);
}

function extractPrimaryQuestionTitle(poll: IPoll | null): string {
  return (
    poll?.pages
      .find((page) => page.pageNumber === 1)
      ?.questions.find((question) => question.number === 1)?.title ?? ''
  );
}

function normalizeMeterData(data: IMeter | null): IMeter | null {
  if (!data) {
    return null;
  }

  return {
    ...data,
    measurmentDatas: [...data.measurmentDatas].sort((a, b) => b.weight - a.weight),
  };
}

function normalizeIncentiveData(
  data: IIncentiveReport[],
): IIncentiveReportPagination[] {
  return data
    .map(
      (incentive) =>
        ({
          ...incentive,
          pagination: {
            page: 1,
            take: 10,
          },
        }) as IIncentiveReportPagination,
    )
    .sort((a, b) => {
      const aExpired = isIncentiveExpired(a.endDate);
      const bExpired = isIncentiveExpired(b.endDate);

      if (aExpired !== bExpired) {
        return aExpired ? 1 : -1;
      }

      return a.title.localeCompare(b.title);
    });
}

function isIncentiveExpired(endDate: string | Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  return end < today;
}

function buildShiftState(
  reportData: IShiftComparativeReport[],
  filter: IReportFilter,
  emptyMessage: string,
): ModuleState<ShiftSection> {
  if (!reportData.length) {
    return emptyState(emptyMessage);
  }

  const prepared = prepareShiftChartData(reportData, filter);
  const labels = prepared.labels ?? [];
  const datasets = prepared.datasets ?? [];

  if (!labels.length || !datasets.length) {
    return emptyState(emptyMessage);
  }

  const { totalResponses, overallSatisfaction } = calculateShiftSummary(
    reportData,
    filter,
  );

  return readyState({
    data: prepared,
    totalResponses,
    overallSatisfaction,
  });
}

function prepareShiftChartData(
  reportData: IShiftComparativeReport[],
  filter: IReportFilter,
): ChartConfiguration<'bar'>['data'] {
  const availableShifts = filter.avaiableShifts ?? [];
  const allShifts = new Set<string>();

  if (availableShifts.length > 0) {
    reportData.forEach((branch) => {
      branch.shiftData.forEach((shift) => {
        if (availableShifts.includes(shift.shiftId)) {
          allShifts.add(shift.shiftDescription);
        }
      });
    });
  } else {
    reportData.forEach((branch) => {
      branch.shiftData.forEach((shift) => {
        allShifts.add(shift.shiftDescription);
      });
    });
  }

  const labels = Array.from(allShifts);
  const datasets = reportData.map((branch) => ({
    label: branch.branchName,
    data: labels.map((shiftDescription) => {
      const shiftData = branch.shiftData.find(
        (shift) => shift.shiftDescription === shiftDescription,
      );
      return shiftData ? shiftData.satisfactionPercentage : 0;
    }),
    responseCount: labels.map((shiftDescription) => {
      const shiftData = branch.shiftData.find(
        (shift) => shift.shiftDescription === shiftDescription,
      );
      return shiftData ? shiftData.responseCount : 0;
    }),
    backgroundColor: '#BE0013',
    borderColor: '#BE0013',
    borderWidth: 1,
  }));

  return {
    labels,
    datasets,
  };
}

function calculateShiftSummary(
  reportData: IShiftComparativeReport[],
  filter: IReportFilter,
): Pick<ShiftSection, 'totalResponses' | 'overallSatisfaction'> {
  const availableShifts = filter.avaiableShifts ?? [];

  if (availableShifts.length > 0) {
    const totalResponses = reportData.reduce((total, branch) => {
      const filteredResponses = branch.shiftData
        .filter((shift) => availableShifts.includes(shift.shiftId))
        .reduce(
          (branchTotal, shift) => branchTotal + shift.responseCount,
          0,
        );

      return total + filteredResponses;
    }, 0);

    const totalSatisfiedResponses = reportData.reduce((total, branch) => {
      const branchSatisfiedResponses = branch.shiftData
        .filter((shift) => availableShifts.includes(shift.shiftId))
        .reduce(
          (branchTotal, shift) => branchTotal + shift.satisfiedResponses,
          0,
        );

      return total + branchSatisfiedResponses;
    }, 0);

    return {
      totalResponses,
      overallSatisfaction:
        totalResponses > 0
          ? Math.round((totalSatisfiedResponses / totalResponses) * 1000) / 10
          : 0,
    };
  }

  const totalResponses = reportData.reduce(
    (total, branch) => total + branch.totalResponses,
    0,
  );

  const totalSatisfiedResponses = reportData.reduce((total, branch) => {
    const branchSatisfiedResponses = branch.shiftData.reduce(
      (branchTotal, shift) => branchTotal + shift.satisfiedResponses,
      0,
    );

    return total + branchSatisfiedResponses;
  }, 0);

  return {
    totalResponses,
    overallSatisfaction:
      totalResponses > 0
        ? Math.round((totalSatisfiedResponses / totalResponses) * 1000) / 10
        : 0,
  };
}
